import { analyze } from "./analyze";
import type { AnalysisData, MetricRecord, SecurityEvent } from "../types";

export type DemoScenario = "mixed" | "normal" | "authentication_bruteforce" | "network_scan" | "suspicious_process_burst";

const SCENARIO_LABELS: Record<DemoScenario, string> = {
  mixed: "six-hour mixed replay",
  normal: "normal workstation",
  authentication_bruteforce: "login attack",
  network_scan: "network scan",
  suspicious_process_burst: "process burst",
};

function random(seed: number): () => number {
  let value = seed >>> 0;
  return () => {
    value = (value * 1664525 + 1013904223) >>> 0;
    return value / 0xffffffff;
  };
}

function ocsf(event: SecurityEvent, classUid: number, activityId: number) {
  return {
    activity_id: activityId,
    activity_name: event.event_type,
    category_uid: Math.floor(classUid / 1000),
    class_uid: classUid,
    class_name: classUid === 1007 ? "Process Activity" : "Authentication",
    severity_id: 1,
    status_id: event.status === "failure" ? 2 : 1,
    time: Date.parse(event.timestamp),
    type_uid: classUid * 100 + activityId,
    metadata: { version: "1.8.0", uid: event.event_id, product: { name: "ueba-detector" } },
  };
}

function scenarioAt(index: number, selected: DemoScenario): DemoScenario | "normal" {
  if (selected === "mixed") {
    if (index >= 118 && index <= 123) return "authentication_bruteforce";
    if (index >= 218 && index <= 224) return "network_scan";
    if (index >= 308 && index <= 313) return "suspicious_process_burst";
    return "normal";
  }
  if (selected === "normal") return "normal";
  return index >= 352 ? selected : "normal";
}

export function createDemoData(selected: DemoScenario = "mixed"): AnalysisData {
  const rng = random(47);
  const now = new Date();
  now.setUTCSeconds(0, 0);
  const start = now.getTime() - 359 * 60_000;
  const metrics: MetricRecord[] = [];
  const events: SecurityEvent[] = [];
  for (let index = 0; index < 360; index += 1) {
    const timestamp = new Date(start + index * 60_000).toISOString();
    const workCycle = Math.sin(index / 35);
    const row: MetricRecord = {
      timestamp,
      host: "demo-workstation",
      cpu_percent: 18 + workCycle * 6 + rng() * 5,
      memory_percent: 57 + Math.sin(index / 90) * 3 + rng() * 2,
      swap_percent: 2 + rng(),
      loadavg_1m: 1.1 + workCycle * 0.3 + rng() * 0.2,
      process_count: 255 + Math.round(workCycle * 9 + rng() * 7),
      user_process_count: 145 + Math.round(workCycle * 6 + rng() * 5),
      thread_count: 900 + Math.round(workCycle * 45 + rng() * 35),
      net_bytes_sent_per_sec: 9000 + rng() * 7000,
      net_bytes_recv_per_sec: 24000 + rng() * 12000,
      net_packets_sent_per_sec: 40 + rng() * 18,
      net_packets_recv_per_sec: 65 + rng() * 25,
      net_errin_per_sec: 0,
      net_errout_per_sec: 0,
      net_dropin_per_sec: 0,
      net_dropout_per_sec: 0,
      disk_read_bytes_per_sec: 45000 + rng() * 60000,
      disk_write_bytes_per_sec: 35000 + rng() * 50000,
      tcp_established: 20 + Math.round(rng() * 8),
      tcp_listen: 8 + Math.round(rng() * 2),
      tcp_syn_sent: Math.round(rng() * 2),
      tcp_close_wait: Math.round(rng()),
      unique_remote_ports: 6 + Math.round(rng() * 3),
      scenario: "normal",
    };
    row.scenario = scenarioAt(index, selected);
    if (row.scenario === "network_scan") {
      const offset = index - (selected === "mixed" ? 218 : 352);
      row.tcp_syn_sent = 70 + offset;
      row.unique_remote_ports = 130 + offset;
      row.net_packets_sent_per_sec = 940 + offset;
    } else if (row.scenario === "suspicious_process_burst") {
      row.process_count = 510;
      row.thread_count = 2700;
      row.cpu_percent = 92;
    }
    metrics.push(row);

    const normalEvent: SecurityEvent = {
      event_id: `demo-process-${index}`,
      timestamp,
      host: row.host,
      event_type: "process_started",
      severity: "informational",
      status: "success",
      source: "demo",
      data: { process: { name: `work-app-${index % 5}` }, actor: { user: { name: "demo-user" } } },
    };
    normalEvent.ocsf = ocsf(normalEvent, 1007, 1);
    events.push(normalEvent);
    if (row.scenario === "authentication_bruteforce") {
      for (let attempt = 0; attempt < 8; attempt += 1) {
        const event: SecurityEvent = {
          event_id: `demo-auth-${index}-${attempt}`,
          timestamp,
          host: row.host,
          event_type: "authentication_failure",
          severity: "medium",
          status: "failure",
          source: "demo",
          data: { actor: { user: { name: "target-user" } }, source_endpoint: { ip: "203.0.113.12" } },
        };
        event.ocsf = ocsf(event, 3002, 1);
        events.push(event);
      }
    }
    if (row.scenario === "suspicious_process_burst") {
      for (let process = 0; process < 24; process += 1) {
        const event: SecurityEvent = {
          event_id: `demo-burst-${index}-${process}`,
          timestamp,
          host: row.host,
          event_type: "process_started",
          severity: "informational",
          status: "success",
          source: "demo",
          data: { process: { name: `burst-tool-${process}` }, actor: { user: { name: "demo-user" } } },
        };
        event.ocsf = ocsf(event, 1007, 1);
        events.push(event);
      }
    }
  }
  return analyze({ sourceName: selected === "mixed" ? "Built-in demonstration" : `Host simulation: ${SCENARIO_LABELS[selected]}`, metrics, events });
}
